import { useEffect, useId, useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { SalesRequest } from "~/types/client";

export const useSalesRequests = (clientId: string) => {
  // Unique ID for this hook instance to prevent Realtime channel name collisions
  const instanceId = useId();

  const [salesRequests, setSalesRequests] = useState<SalesRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedSalesRequest, setSelectedSalesRequest] =
    useState<SalesRequest | null>(null);

  const fetchSalesRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("sales_request")
        .select(
          "*, assigned_transportista, factura_documents:sales_request_documents!sales_request_id(bsale_number, document_type)"
        )
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform database format to frontend format
      const formattedData: SalesRequest[] = data.map((item) => ({
        id: item.id,
        clientId: item.client_id,
        createdBy: item.created_by,
        approvedRejectedBy: item.approved_rejected_by,
        status: item.status as "pending" | "accepted" | "rejected",
        paid: item.paid,
        paymentTerms: item.payment_terms,
        paymentType: item.payment_type as "future" | "prepaid",
        paymentDueDate: item.payment_due_date,
        totalAmount: item.total_amount,
        comments: item.comments,
        internal_notes: item.internal_notes,
        factura_observations: item.factura_observations,
        deliveryDate: item.delivery_date,
        deliveryAddressId: item.delivery_address_id,
        deliveryStreet: item.delivery_street,
        deliveryNumber: item.delivery_number,
        deliveryComplement: item.delivery_complement,
        deliveryCity: item.delivery_city,
        deliveryState: item.delivery_state,
        deliveryComuna: item.delivery_comuna,
        rejectedReason: item.rejected_reason,
        assigned_transportista: item.assigned_transportista,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        salesConditionId: item.sales_condition_id, // Map the sales condition ID
        factura_number: (() => {
          // Extract factura number from the embedded documents
          const facturaDoc = item.factura_documents?.find(
            (doc: any) => doc.document_type === "factura" && doc.bsale_number
          );
          return facturaDoc?.bsale_number || undefined;
        })(),
      }));

      setSalesRequests(formattedData);
    } catch (error) {
      console.error("Error fetching sales requests:", error);
    } finally {
      setLoading(false);
    }
  };

  // Set up real-time subscription for client's sales requests
  useEffect(() => {
    if (clientId) {
      const handleRealtimeUpdates = () => {
        const channel = supabase
          .channel(`client-sales-requests-${clientId}-${instanceId}`)
          .on(
            "postgres_changes",
            {
              event: "*", // Listen for all events (INSERT, UPDATE, DELETE)
              schema: "public",
              table: "sales_request",
              filter: `client_id=eq.${clientId}`,
            },
            async (payload) => {
              console.log("Real-time update received for client:", payload);
              await fetchSalesRequests();

              // Show toast notification based on the event
              if (payload.eventType === "UPDATE") {
                const newStatus = payload.new.status;
                const oldStatus = payload.old.status;
                const newPaid = payload.new.paid;
                const oldPaid = payload.old.paid;

                if (newStatus !== oldStatus) {
                  const notificationMessage =
                    newStatus === "accepted"
                      ? "Una nota de venta ha sido aprobada"
                      : newStatus === "rejected"
                        ? "Una nota de venta ha sido rechazada"
                        : "Una nota de venta ha cambiado de estado";

                  toast({
                    title: "Actualización en tiempo real",
                    description: notificationMessage,
                  });
                } else if (newPaid !== oldPaid) {
                  const notificationMessage = newPaid
                    ? "Una nota de venta ha sido marcada como pagada"
                    : "Una nota de venta ha sido marcada como pendiente de pago";

                  toast({
                    title: "Actualización en tiempo real",
                    description: notificationMessage,
                  });
                }
              }
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      };

      const unsubscribe = handleRealtimeUpdates();
      fetchSalesRequests();

      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [clientId]);

  const filteredSalesRequests = salesRequests.filter((request) => {
    const matchesSearch =
      request.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.createdAt.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      request.status === statusFilter ||
      (statusFilter === "paid" && request.paid);

    return matchesSearch && matchesStatus;
  });

  return {
    salesRequests,
    filteredSalesRequests,
    loading,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    selectedSalesRequest,
    setSelectedSalesRequest,
    refetchSalesRequests: fetchSalesRequests,
  };
};
